import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Award, Sparkles, ArrowRight, Clock, CheckCircle } from "lucide-react";
import ResponsiveAppBar from "../components/AppBar";
import Footer from "../components/Footer";

const BranchSelection = () => {
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleBranchSelect = (branchId, branchName) => {
    if (!["branchOne", "branchTwo"].includes(branchId)) {
      setError("❌ Invalid branch selected. Please choose a valid branch.");
      return;
    }
    sessionStorage.setItem("selectedBranch", JSON.stringify({ branchId, branchName }));
    navigate("/login");
  };

  const branches = [
    {
      id: "branchOne",
      name: "Velavan Finance Elampillai",
      shortName: "Elampillai",
      type: "Main Flagship Branch",
      description: "Premier headquarters with full-suite gold, vehicle, and chit solutions.",
      badge: "Main Branch",
      icon: Award,
      hours: "Mon - Sat: 9:00 AM - 6:00 PM",
      services: [
        "Instant Gold Loan Appraisals",
        "Vehicle Finance & Fast Disbursals",
        "Chit Fund Scheme Enrollment",
        "Dedicated Account Management",
      ],
    },
    {
      id: "branchTwo",
      name: "Velavan Finance Attayampatti",
      shortName: "Attayampatti",
      type: "Regional Digital Branch",
      description: "Fast-track branch with express approvals and quick customer support.",
      badge: "Digital Branch",
      icon: Sparkles,
      hours: "Mon - Sat: 9:30 AM - 5:30 PM",
      services: [
        "Express Gold Loans",
        "Instant EMI Payments & Statements",
        "Monthly Savings Schemes",
        "Direct Advisory Services",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] via-[#f1f5f9] to-[#eff6ff] text-slate-900 flex flex-col justify-between relative overflow-hidden font-sans">
      <ResponsiveAppBar />

      {/* Glowing Ambient Background Orbs */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#936097]/10 rounded-full filter blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-[#77c8c7]/15 rounded-full filter blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-6 py-14 max-w-5xl relative z-10 flex-1">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-slate-50 to-teal-50/40 border border-slate-200/60 mb-4 shadow-sm">
            <img
              src="/mainlogo.png"
              alt="Velavan Finance"
              className="h-12 w-auto object-contain drop-shadow-sm"
            />
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-2">
            Select Your Branch Portal
          </h1>
          <p className="text-slate-600 text-sm sm:text-base max-w-md mx-auto">
            Choose your operating branch location to proceed with your staff/user session.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="max-w-xl mx-auto mb-8 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-sm text-center font-medium">
            {error}
          </div>
        )}

        {/* Branch Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {branches.map((branch) => {
            const IconComp = branch.icon;
            return (
              <div
                key={branch.id}
                className="group relative bg-white border border-slate-200/80 rounded-3xl p-8 shadow-xl shadow-slate-200/60 hover:border-[#77c8c7] hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#936097] to-[#77c8c7] flex items-center justify-center text-white shadow-lg shadow-[#936097]/20 group-hover:scale-105 transition-transform">
                      <IconComp className="w-7 h-7" />
                    </div>
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-teal-50 border border-teal-200/60 text-[#0d9488]">
                      {branch.badge}
                    </span>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 mb-1">
                    {branch.shortName}
                  </h2>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0d9488] uppercase tracking-wider mb-4">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{branch.type}</span>
                  </div>

                  <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                    {branch.description}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-slate-600 mb-6 bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <Clock className="w-4 h-4 text-[#0d9488] shrink-0" />
                    <span>{branch.hours}</span>
                  </div>

                  <div className="space-y-2.5 mb-8 text-xs sm:text-sm text-slate-700">
                    {branch.services.map((srv, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#0d9488] shrink-0" />
                        <span>{srv}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleBranchSelect(branch.id, branch.name)}
                  className="w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#936097] via-[#854d8a] to-[#77c8c7] hover:opacity-95 shadow-lg shadow-[#936097]/25 transition-all flex items-center justify-center gap-2 cursor-pointer uppercase text-xs tracking-wider"
                >
                  <span>Select {branch.shortName} Branch</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BranchSelection;
